import { SocketClient } from "cbcore-ts"
import { CALL, IF, IS_NOT_LIKE_NULL, NO, RETURNER, UIRoute, UITextView, UIView, UIViewController, YES } from "uicore-ts"
import { CBColor } from "./Custom components/CB/CBColor"
import { CBDataViewCellDescriptor } from "./Custom components/CB/CBDataView"
import { CellView } from "./Custom components/CellView"
import { RHDataView } from "./Custom components/RH/RHDataView"
import { RHLabeledSearchableDropdown } from "./Custom components/RH/RHLabeledSearchableDropdown"
import { RHEuroFormatter } from "./Custom components/RH/Utils_slop/RHEuroFormatter"
import { StatisticsPageDesignTokens } from "./Custom components/RH/Utils_slop/StatisticsPageDesignTokens"
import { StatisticsPageViewStyling } from "./Custom components/RH/Utils_slop/StatisticsPageViewStyling"
import { LanguageService } from "./LanguageService"
import { RHFont, RHSpacing, RHTables } from "./Custom components/RH/Utils_slop/RHDesignTokens"


const numbersArrayWithRange = (start: number, end: number) => Array.from({ length: (end - start) }, (v, k) => k + start)

export const numberFormatter = new Intl.NumberFormat(
    "et-EE",
    {
        maximumFractionDigits: 1
    }
)
export const currencyFormatter = RHEuroFormatter.currencyFormatter()
export const percentFormatter = new Intl.NumberFormat(
    "et-EE",
    {
        style: "percent",
        maximumFractionDigits: 1
    }
)


// One table row: the statistic's name, then a value per period under keys like "2024_statValue".
type StatisticsSubRow = { isSubDataItem: boolean } & Record<string, unknown>
type StatisticsRow = { statName: string, subData?: StatisticsSubRow[] } & Record<string, unknown>


export class RHRStatisticsViewController extends UIViewController {

    entityType?: "procurer" | "offerer" // Default, will be set by route
    _ignoreSelectionDidChange = NO

    get titleKey(): string {

        const result = "" + this.class.name + "Title_" + (this.entityType ?? "procurer")
        return result

    }

    explanationLabel: UITextView = new UITextView().addedAsSubviewToView(this.view).configuredWithObject({
        // Text will be set dynamically based on entityType
        textAlignment: UITextView.textAlignment.center,
        isSingleLine: NO,
        textColor: StatisticsPageDesignTokens.explanationTextColor,
        style: {
            fontFamily: RHFont.familyBase,
            fontSize: RHFont.sizeXSmall,
            fontWeight: StatisticsPageDesignTokens.explanationFontWeight,
            lineHeight: RHFont.lineHeightBase
        }
    })

    comparisonEntityDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            // Label text will be set dynamically in handleRoute
            dropdown: {
                data: [], // Data will be loaded dynamically based on entityType in handleRoute
                dialogContainerView: this.view,
                isSingleSelection: YES,
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (this._ignoreSelectionDidChange) {
                            return
                        }

                        if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                            const route = UIRoute.currentRoute
                            const selectedCode = this.comparisonEntityDropdown.selectedItemCodes.firstElement ?? ""

                            // Add the correct parameter based on entityType
                            const parameterName = IF(this.entityType === "procurer")(() =>
                                RHRStatisticsViewController.ParameterIdentifierName.procurer
                            ).ELSE(() =>
                                RHRStatisticsViewController.ParameterIdentifierName.offerer
                            )
                            const updatedRoute = route.routeBySettingParameterInComponent(
                                RHRStatisticsViewController.routeComponentName,
                                parameterName,
                                selectedCode
                            )

                            // Determine if we should replace history or push new entry
                            const existingParameter = route.componentWithViewController(this.class)?.parameters[parameterName]

                            if (existingParameter) {
                                updatedRoute.apply()
                            }
                            else {
                                updatedRoute.applyByReplacingCurrentRouteInHistory()
                            }

                        }
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)

    periodDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            label: { text: "Perioodid" },
            dropdown: {
                data: numbersArrayWithRange(2023, new Date().getFullYear() + 1).map(name => {
                        return {
                            _id: name + "",
                            title: LanguageService.localizedTextObjectForText(name + ""),
                            isADropdownDataSection: NO,
                            isADropdownDataRow: YES,
                            attachedObject: name + "",
                            itemCode: name + "",
                            dropdownCode: "Periods"
                        }
                    }
                ),
                // selectedItemCodes: UIRoute.currentRoute.componentWithName(
                //         "rhrstatistics"
                //     )?.parameters?.periods?.split("+") ||
                //     numbersArrayWithRange(
                //         new Date().getFullYear() - 1,
                //         new Date().getFullYear() + 1
                //     ).map(number => number + ""),
                dialogContainerView: this.view,
                isSingleSelection: NO,
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (this._ignoreSelectionDidChange) {
                            return
                        }

                        if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                            const route = UIRoute.currentRoute
                                .routeBySettingParameterInComponent(
                                    RHRStatisticsViewController.routeComponentName,
                                    RHRStatisticsViewController.ParameterIdentifierName.periods,
                                    this.periodDropdown.selectedItemCodes.join("+") ?? ""
                                )

                            if (UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.periods]) {
                                route.apply()
                            }
                            else {
                                route.applyByReplacingCurrentRouteInHistory()
                            }
                        }
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)

    procurementProcedureCodeDropdown = new RHLabeledSearchableDropdown<string>()
        .configuredWithObject({
            label: { text: "Menetlusliik" },
            dropdown: {
                data: [], // Data loaded dynamically
                dialogContainerView: this.view,
                isSingleSelection: NO, // Allow multiple selections
                controlEventTargetAccumulator: {
                    SelectionDidChange: () => {
                        if (this._ignoreSelectionDidChange) {
                            return
                        }

                        if (UIRoute.currentRoute.componentWithViewController(this.class)) {
                            const route = UIRoute.currentRoute
                                .routeBySettingParameterInComponent(
                                    RHRStatisticsViewController.routeComponentName,
                                    RHRStatisticsViewController.ParameterIdentifierName.procType, // Use new param name
                                    this.procurementProcedureCodeDropdown.selectedItemCodes.join("+") ?? "" // Join selected codes
                                )

                            // Decide whether to replace or push history based on if the param already exists
                            if (UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]) {
                                route.apply()
                            }
                            else {
                                route.applyByReplacingCurrentRouteInHistory()
                            }
                        }
                        // Trigger data update when selection changes
                        this.updateStatisticsView()
                    }
                }
            },
            enabled: NO
        }).addedAsSubviewToView(this.view)

    statisticsView = new RHDataView<StatisticsRow>()
        .configuredWithObject({
            // isSearchAvailable: NO,
            isTreeView: YES
        }).addedAsSubviewToView(this.view)
    bottomView = new UIView().addedAsSubviewToView(this.view)

    constructor(view: UIView) {

        super(view)

        // Code for further setup if necessary

        this.view.configureWithObject({
            backgroundColor: StatisticsPageDesignTokens.pageBackgroundColor
        })
        this.statisticsView.noDataMessage = StatisticsPageDesignTokens.emptyStateMessageSingle

    }


    private updateUITextsBasedOnEntityType() {
        if (this.entityType === "procurer") {
            this.explanationLabel.text = "Siin on võimalik näha Riigihangete registri avaliku info põhjal koostatud statistikat valitud hankija kohta."
        }
        else {
            this.explanationLabel.text = "Siin on võimalik näha Riigihangete registri avaliku info põhjal koostatud statistikat valitud pakkuja kohta."
        }
        this.view.setNeedsLayout() // Ensure layout updates if text changes size
    }

    private async retrieveComparisonEntityNamesAndInitDropdown() {

        let entityNames: string[] = []
        let dropdownCode: string
        if (this.entityType === "procurer") {
            entityNames = (await SocketClient.RetrieveProcurerNames()).result
            this.comparisonEntityDropdown.label.text = "Hankija"
            dropdownCode = "ProcurerNames"
        }
        else if (this.entityType === "offerer") {
            entityNames = (await SocketClient.RetrieveOffererNames()).result
            this.comparisonEntityDropdown.label.text = "Pakkuja"
            dropdownCode = "OffererNames"
        }
        this.comparisonEntityDropdown.dropdown.data = entityNames.map(name => ({
            _id: name,
            title: LanguageService.localizedTextObjectForText(name),
            isADropdownDataSection: NO,
            isADropdownDataRow: YES,
            attachedObject: name,
            itemCode: name,
            dropdownCode: dropdownCode // Use explicitly set dropdownCode
        }))

        this.comparisonEntityDropdown.enabled = YES

    }

    private async retrieveProcurementProcedureCodesAndInitDropdown() {

        try {

            const procedureCodes = (await SocketClient.RetrieveProcurementProcedureCodes()).result
            const data = Object.entries(procedureCodes).map(([code, name]) => ({
                _id: code,
                title: LanguageService.localizedTextObjectForKey(
                    code + "_procurement_type",
                    name
                ),
                isADropdownDataSection: NO,
                isADropdownDataRow: YES,
                attachedObject: code,
                itemCode: code,
                dropdownCode: "ProcurementProcedureCodes"
            }))
            this.procurementProcedureCodeDropdown.dropdown.data = data

            const routeProcurementTypes = UIRoute.currentRoute
                .componentWithViewController(this.class)
                ?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]
            let selectedProcurementTypes = Object.keys(procedureCodes)
            if (routeProcurementTypes !== undefined) {
                selectedProcurementTypes = decodeURIComponent(routeProcurementTypes).split("+").filter(p => p)
            }

            const previousIgnoreSelectionDidChange = this._ignoreSelectionDidChange
            this._ignoreSelectionDidChange = YES
            this.procurementProcedureCodeDropdown.selectedItemCodes = selectedProcurementTypes
            this._ignoreSelectionDidChange = previousIgnoreSelectionDidChange

            // const procurementProcedureCodes = UIRoute.currentRoute.componentWithViewController(this.class)?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]
            // UIRoute.currentRoute.routeBySettingParameterInComponent(
            //     RHRStatisticsViewController.routeComponentName,
            //     RHRStatisticsViewController.ParameterIdentifierName.procType,
            //     procurementProcedureCodes || procedureCodes.join("+")
            // ).applyByReplacingCurrentRouteInHistory()

            this.procurementProcedureCodeDropdown.enabled = YES

        } catch (error) {
            console.error("Error fetching procurement types:", error)
        }

    }

    async updateStatisticsView() {

        const routeComponent = this.routeComponent
        const entityName = routeComponent
            ?.parameters[routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.entityType]]
        if (!entityName || !this.periodDropdown.dropdown.selectedItemCodes.length || !this.procurementProcedureCodeDropdown.selectedItemCodes.length) {
            this.statisticsView.data = []
            this.statisticsView.descriptors = []
            this.statisticsView.noDataMessage = StatisticsPageDesignTokens.emptyStateMessageSingle
            return
        }

        const periods = this.periodDropdown.selectedItemCodes
            .map(period => period.integerValue)
            .sort()
            .map(period => period + "")
        const descriptors: CBDataViewCellDescriptor<StatisticsRow>[] = [
            {
                keyPath: "statName",
                title: "",
                allowSorting: NO,
                updateCell: (
                    cellView: CellView,
                    dataItem: StatisticsRow
                ) => {

                    StatisticsPageViewStyling.applyTableCellChrome(cellView)
                    cellView.titleLabel!.text = LanguageService.stringForKey(
                        dataItem.statName,
                        LanguageService.currentLanguageKey,
                        dataItem.statName
                    )
                    cellView.titleLabel!.textColor = RHTables.rowTextColor

                }
            }
        ]

        periods.forEach(period => {
            const periodPrefix = period + "_"
            const descriptor: CBDataViewCellDescriptor<StatisticsRow> = {
                keyPath: periodPrefix + "statValue",
                title: period,
                allowSorting: NO,
                titleLabelAlignment: UITextView.textAlignment.center,
                updateCell: (cellView, dataItem) => {

                    StatisticsPageViewStyling.applyTableCellChrome(cellView)
                    cellView.backgroundColor = IF(dataItem.isSubDataItem)(
                        RETURNER(StatisticsPageDesignTokens.tableSubRowBackgroundColor)
                    ).ELSE(
                        RETURNER(StatisticsPageDesignTokens.chartPanelBackgroundColor)
                    )
                    cellView.titleLabel!.textColor = IF(dataItem.isSubDataItem)(
                        RETURNER(StatisticsPageDesignTokens.tableSubRowTextColor)
                    ).ELSE(
                        RETURNER(RHTables.rowTextColor)
                    )

                }
            }
            descriptors.push(descriptor)
        })

        this.statisticsView.descriptors = descriptors
        const statisticsTableColumnWeights = [
            420,
            ...periods.map(() => 220)
        ]
        this.statisticsView.tableHeaderView.cellWeights = statisticsTableColumnWeights
        this.statisticsView.tableView.cellWeights = statisticsTableColumnWeights
        this.statisticsView.hasResizableColumns = YES
        this.statisticsView.updateRowWithNestingDepth = (nestingDepth, rowView) => {

            // If there is depth, add another subview to the background view

            if (nestingDepth && !rowView.backgroundView.subviews.length) {

                rowView.backgroundView.addSubview(new UIView(
                    rowView.elementID + "_BackgroundViewNestingDepthIndicatorShaderView_added_from_" + this.class.name
                ).configuredWithObject({
                    backgroundColor: CBColor.blackColor,
                    setPosition: CALL(0, 0, 0, 0)
                }))

            }
            else if (nestingDepth && rowView.backgroundView.subviews.length) {

                rowView.backgroundView.subviews.firstElement.alpha = nestingDepth * 0.08

            }
            else if (!nestingDepth) {

                rowView.backgroundView.subviews.everyElement.removeFromSuperview()

            }

        }

        let statisticsViewData: (StatisticsRow)[] = []
        const procurementProcedureCodes = this.procurementProcedureCodeDropdown.selectedItemCodes
        // Check if at least one procurement type is selected
        if (procurementProcedureCodes.length === 0) {
            this.statisticsView.data = [] // Clear data if no type selected
            this.statisticsView.noDataMessage = StatisticsPageDesignTokens.emptyStateMessageSingle
            return
        }

        let statisticsObject: Record<string, unknown>
        this.statisticsView.loading = YES
        this.view.setNeedsLayoutUpToRootView()
        UIView.layoutViewsIfNeeded()
        await new Promise<void>(resolve => requestAnimationFrame(() => resolve()))
        try {
            if (this.entityType === "procurer") {
                statisticsObject = (await SocketClient.ProcurerStatisticsForPeriods({
                    procurerName: entityName,
                    periods: periods,
                    procurementProcedureCodes: procurementProcedureCodes,
                    selfTest: routeComponent?.parameters.selfTest == "YES"
                })).result
            }
            else { // entityType === "offerer"
                statisticsObject = (await SocketClient.OffererStatisticsForPeriods({
                    offererName: entityName,
                    periods: periods,
                    procurementProcedureCodes: procurementProcedureCodes,
                    selfTest: routeComponent?.parameters.selfTest == "YES"
                })).result
            }
        }
        catch (error) {
            this.statisticsView.loading = NO
            throw error
        }
        // Keys are expected to be in the format {year}_{statName}
        // Need to extract the unique stat names across the selected periods.
        // We need to extract the unique stat names across the selected periods.

        const allKeys = statisticsObject.allKeys as string[]

        // Figure out the unique stat names (part after year_)
        // Filter keys based on selected periods
        const rowKeys = [
            ...new Set(
                allKeys
                    .filter(key => {
                        const mainKey = (key + "").split("_").lastElement
                        if (mainKey.startsWith("percentageOf")) {
                            if (allKeys.contains(key.replace("percentageOf", "numberOf"))) {
                                return NO
                            }
                        }
                        return YES
                    })
                    .map((key: string) => {
                        // Extract the stat name part (e.g., contractMonetarySum)
                        const parts = key.split("_")
                        return parts.lastElement
                    }).filter((key: undefined | string) => key) // Filter out undefined/empty keys
            )
        ]

        // Add values into the rows
        statisticsViewData = rowKeys.map((key: string) => {
            const result: StatisticsRow = {
                statName: LanguageService.stringForKey(key, LanguageService.currentLanguageKey, key)
            }
            periods.forEach(period => {
                const fullKey = `${period}_${key}`
                const value = statisticsObject[fullKey]
                let displayValue: string | number = "–" // Default when data is unavailable
                function isAnArrayOfStrings(value: StatisticsRow[string]): value is string[] {
                    return Array.isArray(value) && value.every(item => typeof item === "string")
                }

                if (key == "offerersDataWithCount") {

                    var asdasdasdasdasjhfdksdikuyhfgf = 1 //value.map(dataObject => dataObject)


                }

                if (key == "offerersDataWithMonetarySum") {

                    var asdasdasdasdasd = 1

                }

                if (value !== undefined && value !== null) {

                    if (key.startsWith("numberOf")) {
                        const numberValue = Number(value)
                        const percentageKey = key.replace("numberOf", "percentageOf")
                        const fullPercentageKey = `${period}_${percentageKey}`
                        const percentageValue = statisticsObject[fullPercentageKey]

                        if (IS_NOT_LIKE_NULL(percentageValue)) {
                            // Both number and percentage exist
                            displayValue = `${numberFormatter.format(numberValue)} (${percentFormatter.format(Number(
                                Number(percentageValue) * 0.01))})`
                        }
                        else {
                            // Only number exists
                            displayValue = numberFormatter.format(numberValue)
                        }
                    }
                    else if (key.startsWith("percentageOf")) {
                        // This handles cases where the 'numberOf' row was filtered out,
                        // but we still want to show the percentage row.
                        displayValue = `${Number(value)}%`
                    }
                    else if (key === "contractMonetarySum" && typeof value === "number") {
                        displayValue = currencyFormatter.format(value)
                    }
                    else if (typeof value === "number") {
                        displayValue = numberFormatter.format(value)
                    }
                    else if (isAnArrayOfStrings(value)) {
                        displayValue = value.join(", ")
                    }
                    else {
                        // Handle other non-numeric string values if necessary
                        displayValue = String(value)
                    }
                }
                // Use a consistent key structure for the data view row item
                result[`${period}_statValue`] = displayValue

                if (isAnArrayOfStrings(value)) {

                    const subData: StatisticsSubRow[] = result.subData ?? []
                    result.subData = subData

                    const stringsToShowAsSubdataItems = value

                    stringsToShowAsSubdataItems.forEach((string, index) => {

                        // Find the corresponding subdata item or add a new one and set the string

                        const subdataItem: StatisticsSubRow = subData[index] ?? { isSubDataItem: YES }

                        subdataItem[`${period}_statValue`] = string.italics()

                        subData[index] = subdataItem

                    })
                }

            })
            return result
        })

        this.statisticsView.data = statisticsViewData
        this.statisticsView.setTreeData(statisticsViewData, "subData", NO)
        this.statisticsView.noDataMessage = IF(statisticsViewData.length)(
            RETURNER("")
        ).ELSE(
            RETURNER("Valitud tingimustele vastavaid statistikaandmeid ei leitud.")
        )


        this.view.setNeedsLayoutUpToRootView()
        await StatisticsPageViewStyling.waitForLoadingCompletionFrameBudget()
        this.statisticsView.loading = NO

    }


    static override readonly routeComponentName = "rhrstatistics"

    static override readonly ParameterIdentifierName = {
        "entityType": "entityType",
        "procurer": "procurer",
        "offerer": "offerer",
        "periods": "periods",
        "procType": "procType",
        "selfTest": "selfTest"
    }

    override async viewWillAppear() {

        this.retrieveProcurementProcedureCodesAndInitDropdown()
        this.retrieveComparisonEntityNamesAndInitDropdown()

        const routePeriods = this.routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.periods]
        let selectedPeriods = numbersArrayWithRange(
            new Date().getFullYear() - 2,
            new Date().getFullYear() + 1
        ).map(name => name + "")
        if (routePeriods !== undefined) {
            selectedPeriods = decodeURIComponent(routePeriods).split("+").filter(period => period)
        }
        const previousIgnoreSelectionDidChange = this._ignoreSelectionDidChange
        this._ignoreSelectionDidChange = YES
        this.periodDropdown.selectedItemCodes = selectedPeriods
        this._ignoreSelectionDidChange = previousIgnoreSelectionDidChange
        this.periodDropdown.enabled = YES

    }

    override async viewDidAppear() {

        const routeComponent = this.routeComponent
        const entityName = routeComponent
            ?.parameters[routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.entityType]]

        if (!entityName) {

            if (!this.comparisonEntityDropdown.dropdown.data.length) {
                await this.retrieveComparisonEntityNamesAndInitDropdown()
            }

            this.comparisonEntityDropdown.dropdown.openDropdown()
            this.comparisonEntityDropdown.dropdown._searchTextField.focus()
            this.comparisonEntityDropdown.dropdown._searchTextField.text = ""

        }

        var asd = 1

    }


    override async viewWillDisappear() {

    }


    override async handleRoute(route: UIRoute) {

        await super.handleRoute(route)

        // Determine entityType from the route parameter, default to 'procurer' if missing
        const entityTypeFromRoute = this.routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.entityType] as "procurer" | "offerer"

        // Set the (default) entity type in the route if it is missing
        if (!entityTypeFromRoute && this.routeComponent) {

            route.routeBySettingParameterInComponent(
                RHRStatisticsViewController.routeComponentName,
                RHRStatisticsViewController.ParameterIdentifierName.entityType,
                "procurer"
            ).applyByReplacingCurrentRouteInHistory()

            return

        }

        // Only update dropdown data if entityType changed
        if (entityTypeFromRoute !== this.entityType) {
            this.entityType = entityTypeFromRoute
            this.updateUITextsBasedOnEntityType()

            console.log("Entity type changed, fetching new names for:", this.entityType) // Add log for debugging
            await this.retrieveComparisonEntityNamesAndInitDropdown()

        }

        // Set selected entity from route (always do this, as the selected entity might change without type changing)
        const selectedEntity = decodeURIComponent(this.routeComponent?.parameters[this.entityType] ?? "")

        if (!this.procurementProcedureCodeDropdown.dropdown.data.length) {
            await this.retrieveProcurementProcedureCodesAndInitDropdown()
        }

        // Set selected periods
        const selectedPeriods = decodeURIComponent(
            this.routeComponent?.parameters.periods ?? ""
        ).split("+").filter(p => p) // Filter out empty strings if periods parameter is empty

        // Set selected procurement types (split by '+') only if data is loaded
        const routeProcurementTypes = this.routeComponent?.parameters[RHRStatisticsViewController.ParameterIdentifierName.procType]
        let selectedProcurementProcedureCodes: string[] = this.procurementProcedureCodeDropdown.dropdown.data
            .map(object => object.itemCode)
        if (routeProcurementTypes !== undefined) {
            selectedProcurementProcedureCodes = decodeURIComponent(routeProcurementTypes).split("+").filter(p => p)
        }

        this._ignoreSelectionDidChange = YES
        this.comparisonEntityDropdown.dropdown.selectedItemCodes = [selectedEntity].filter(p => p)
        this.periodDropdown.dropdown.selectedItemCodes = selectedPeriods
        this.procurementProcedureCodeDropdown.dropdown.selectedItemCodes = selectedProcurementProcedureCodes
        this._ignoreSelectionDidChange = NO

        await this.updateStatisticsView()


    }


    override updateViewConstraints() {

        super.updateViewConstraints()

    }


    override updateViewStyles() {

        super.updateViewStyles()

    }


    override viewDidLayoutSubviews() {

        super.viewDidLayoutSubviews()

    }


    override layoutViewSubviews() {

        super.layoutViewSubviews()

        const bounds = this.view.contentBoundsWithInset(RHSpacing.large)
            .rectangleWithMaxWidth(StatisticsPageDesignTokens.wideContentMaxWidth, 0.5)
        const dropdownGap = StatisticsPageDesignTokens.filterControlGap
        const preferredDropdownWidths = [
            500,
            300,
            500
        ]
        const dropdownWidths = IF((preferredDropdownWidths.summedValue + dropdownGap * 2) <= bounds.width)(() =>
            preferredDropdownWidths
        ).ELSE(() => {
            const availableWidth = bounds.width - dropdownGap * 2
            return [
                availableWidth * 0.38,
                availableWidth * 0.24,
                availableWidth * 0.38
            ]
        })
        const dropdownHeight = [
            this.comparisonEntityDropdown.intrinsicContentHeight(dropdownWidths[0]),
            this.periodDropdown.intrinsicContentHeight(dropdownWidths[1]),
            this.procurementProcedureCodeDropdown.intrinsicContentHeight(dropdownWidths[2]),
            StatisticsPageDesignTokens.dropdownMinHeight
        ].max()
        const explanationHeight = [
            this.explanationLabel.intrinsicContentHeight(bounds.width),
            StatisticsPageDesignTokens.explanationMinHeight
        ].max()
        const controlsWidth = [
            bounds.width,
            dropdownWidths.summedValue + dropdownGap * (dropdownWidths.length - 1)
        ].min()
        const statisticsViewHeight = [
            [
                this.statisticsView.intrinsicContentHeight(bounds.width),
                420
            ].max(),
            760
        ].min()

        bounds
            .rectangleWithHeight(explanationHeight)
            .rectangleWithWidth([bounds.width, StatisticsPageDesignTokens.explanationMaxWidth].min(), 0.5)
            .assignedAsFrameOfView(this.explanationLabel)
            .rectangleForNextRow(
                StatisticsPageDesignTokens.explanationBottomGap,
                dropdownHeight
            )
            .rectangleWithWidth(controlsWidth, 0.5)
            .performingFunctionWithSelf(filterFrame => filterFrame.distributeViewsAlongWidth(
                [this.comparisonEntityDropdown, this.periodDropdown, this.procurementProcedureCodeDropdown],
                undefined,
                dropdownGap,
                dropdownWidths
            ))
            .rectangleWithWidth(bounds.width)
            .rectangleWithX(bounds.x)
            .rectangleForNextRow(
                RHSpacing.large,
                statisticsViewHeight
            )
            .assignedAsFrameOfView(this.statisticsView)
            .rectangleForNextRow(
                RHSpacing.large,
                RHSpacing.xLarge
            )
            .assignedAsFrameOfView(this.bottomView)


    }
}

